import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import useVariations from 'packages/pages/design/hooks/useVariations';
import { pureElementsSelector } from 'packages/store/creativeEditor/creativeEditor.selectors';
import { flattenTree } from 'packages/common/editor/helpers/elementtree';
import { framesToMilliseconds, millisecondToFrames } from 'packages/common/editor/editorHelper';

export const MAX_VIDEO_DURATION = 5 * 60 * 25;

type UseTrimCorrectionParams = {
    elementId: number;
    isEditorPage: boolean;
    isSlipMode: boolean;
    fullDuration: number; // ms
    elementDuration: number; // ms (clip duration in the asset)
    frameRate: number;
    start: number; // frames (current start/offset)
    end: number; // frames (current end)
    fullDurationInFrames: number;
};

export const useTrimCorrection = ({
    elementId,
    isEditorPage,
    isSlipMode,
    fullDuration,
    elementDuration,
    frameRate,
    start,
    end,
    fullDurationInFrames,
}: UseTrimCorrectionParams) => {
    const elements: any = useSelector(pureElementsSelector);
    const { variations, currentVariation } = useVariations();

    const maxValueForSlipMode = useMemo(() => fullDuration - elementDuration, [fullDuration, elementDuration]);

    const originalVideoLength: number = useMemo(
        () => (!elements || isEditorPage ? 0 : flattenTree(elements)[elementId]?.properties.duration),
        [elements, elementId, isEditorPage],
    );

    const isDynamicLengthEnabled = useMemo(() => {
        if (!elements || isEditorPage) {
            return false;
        }

        return flattenTree(elements)[elementId]?.properties.useDynamicLength;
    }, [elements, elementId, isEditorPage]);

    const maxDynamicLength = useMemo(() => {
        if (!currentVariation || !variations) {
            return MAX_VIDEO_DURATION;
        }

        const currentVariationData = variations.find((v) => v.id === currentVariation);
        const { elements: updatedElements } = Object.values(currentVariationData.sizes)[0];
        const totalOtherElsLength = Object.keys(updatedElements).reduce((accum, currentElId) => {
            if (Number(currentElId) === elementId) {
                return accum;
            }

            const currentEl = updatedElements[currentElId];

            return accum + (currentEl.duration || 0);
        }, 0);

        return MAX_VIDEO_DURATION - totalOtherElsLength;
    }, [elementId, variations, currentVariation]);

    const correctStartFrame = (frame: number): number => {
        const chosenFrameInTime: number = framesToMilliseconds(frame, frameRate);
        let desiredStartFrame =
            isSlipMode && chosenFrameInTime > maxValueForSlipMode
                ? millisecondToFrames(maxValueForSlipMode, frameRate)
                : frame;
        const desiredEndFrame = isSlipMode ? desiredStartFrame + millisecondToFrames(elementDuration, frameRate) : end;

        // start-value can't be more than end-value - so just leave it to zero
        if (desiredStartFrame >= desiredEndFrame) {
            desiredStartFrame = 0;
        }

        const expectedVideoLength = desiredEndFrame - desiredStartFrame;

        if (expectedVideoLength < originalVideoLength || expectedVideoLength > maxDynamicLength) {
            desiredStartFrame = desiredEndFrame - originalVideoLength;
        }

        return desiredStartFrame;
    };

    const correctEndFrame = (frame: number): number => {
        let desiredEndFrame: number;

        if (!isSlipMode) {
            desiredEndFrame = frame <= start || frame > fullDurationInFrames ? fullDurationInFrames : frame;

            const expectedVideoLength = desiredEndFrame - start;

            if (expectedVideoLength < originalVideoLength || expectedVideoLength > maxDynamicLength) {
                desiredEndFrame = start + originalVideoLength;
            }
        } else {
            const minValueForEndFrame = millisecondToFrames(elementDuration, frameRate);
            desiredEndFrame = Math.max(minValueForEndFrame, Math.min(frame, fullDurationInFrames));
        }

        return desiredEndFrame;
    };

    return { correctStartFrame, correctEndFrame, originalVideoLength, maxDynamicLength, isDynamicLengthEnabled };
};
